import { CadyAddOn } from '@/lib/cady/cadyEnums'

import type {
  CartBundleStorefrontDto,
  CartItemStorefrontDto,
  CatalogBundleStorefrontDto
} from '@VelocitySquared/sunny16-commerce-dataobjects/storefront'

export const CADY_SESSION_TYPE_ID_ATTR = 'CADY_SessionTypeId'
export const CADY_SCENE_ID_ATTR = 'CADY_SceneId'
/**
 * Backend-set product identifier on items. Despite the name, this attribute
 * is present on items inside *any* bundle role — AddOn, PrePay, CartUpsell,
 * and even some Scene items (e.g. SCENE_OUTDOOR carries `CADY_AddOnId=1`
 * because Outdoor is also sold as an add-on). Treat it as "Cady item
 * product ID", not "add-on-only ID". Names match the wire format set by
 * the C# catalog, so we keep them aligned.
 */
export const CADY_ADDON_ID_ATTR = 'CADY_AddOnId'

export function hasAttribute(cartItem: CartItemStorefrontDto, key: string): boolean {
  return (cartItem.item?.attributes ?? []).some((a) => a.key === key)
}

/**
 * Walks every itemGroup → item in a catalog bundle and returns the set of
 * `CADY_AddOnId` values present, mapped to `CadyAddOn` enum members.
 *
 * Used to identify add-on / pre-pay bundles by the *items* they contain
 * rather than by the (mutable) bundle identifier string. A combo H/M
 * bundle returns `Set{Hair, Makeup}`; a Digital + Portfolio pre-pay
 * returns `Set{AllDigitals, Portfolio}`.
 *
 * @param itemIdentifierFilter When provided, only items whose `identifier`
 *   matches the filter are included. Used by callers walking "pick one"
 *   bundles (e.g. the EP `HAIR_OR_MAKEUP` toggle, which contains both Hair
 *   and Makeup items in a single itemGroup with `maxAllowedItems=1`) where
 *   only the user-selected item is actually attached.
 */
export function getBundleAddOnIds(
  catalogBundle: CatalogBundleStorefrontDto,
  itemIdentifierFilter?: string
): Set<CadyAddOn> {
  const ids = new Set<CadyAddOn>()
  const itemGroups = catalogBundle.bundle?.itemGroups ?? []
  for (const group of itemGroups) {
    for (const groupItem of group.itemGroupItems ?? []) {
      if (itemIdentifierFilter && groupItem.item?.identifier !== itemIdentifierFilter) continue
      const attr = groupItem.item?.attributes?.find((a) => a.key === CADY_ADDON_ID_ATTR)
      if (!attr) continue
      const numeric = Number(attr.value)
      if (Number.isFinite(numeric) && numeric in CadyAddOn) {
        ids.add(numeric as CadyAddOn)
      }
    }
  }
  return ids
}

/**
 * Walks every cart item in a cart bundle and returns the set of
 * `CADY_AddOnId` values present, mapped to `CadyAddOn` enum members.
 * Cart-side counterpart to `getBundleAddOnIds` (which walks the catalog
 * bundle's itemGroups).
 */
export function getCartBundleAddOnIds(cartBundle: CartBundleStorefrontDto): Set<CadyAddOn> {
  const ids = new Set<CadyAddOn>()
  for (const cartItem of cartBundle.cartItems) {
    const attr = cartItem.item?.attributes?.find((a) => a.key === CADY_ADDON_ID_ATTR)
    if (!attr) continue
    const numeric = Number(attr.value)
    if (Number.isFinite(numeric) && numeric in CadyAddOn) {
      ids.add(numeric as CadyAddOn)
    }
  }
  return ids
}

/**
 * Find the first cart bundle whose items carry the given `CADY_AddOnId`
 * value. Used to locate a cart-resident bundle by its product role (e.g.
 * Artist Touch) without depending on the mutable bundle identifier string.
 */
export function findCartBundleByAddOnId(
  cartBundles: CartBundleStorefrontDto[],
  addOnId: CadyAddOn
): CartBundleStorefrontDto | null {
  return cartBundles.find((cb) => getCartBundleAddOnIds(cb).has(addOnId)) ?? null
}

/**
 * True when this cart bundle is one of the prepay-*card* products
 * (Digital Downloads or Digital + Portfolio) — identified by `AllDigitals`
 * or `Portfolio` items. Premier Retouching is a prepay-role bundle too
 * but is not a card, so it is explicitly excluded.
 */
export function isPrepayCardCartBundle(cb: CartBundleStorefrontDto): boolean {
  const ids = getCartBundleAddOnIds(cb)
  if (ids.has(CadyAddOn.PremierRetouching)) return false
  return ids.has(CadyAddOn.AllDigitals) || ids.has(CadyAddOn.Portfolio)
}

/**
 * A cart bundle is the session bundle when any of its cart items carries the
 * `CADY_SessionTypeId` attribute. Only session bundles carry this attribute.
 */
export function isSessionBundle(cb: CartBundleStorefrontDto): boolean {
  return cb.cartItems.some((ci) => hasAttribute(ci, CADY_SESSION_TYPE_ID_ATTR))
}

export function findSessionBundle(
  cartBundles: CartBundleStorefrontDto[]
): CartBundleStorefrontDto | null {
  return cartBundles.find(isSessionBundle) ?? null
}

/**
 * Scene cart items live inside the session bundle and are identified by the
 * `CADY_SceneId` attribute on the underlying `item`.
 */
export function extractSceneItems(sessionBundle: CartBundleStorefrontDto): CartItemStorefrontDto[] {
  return sessionBundle.cartItems.filter((ci) => hasAttribute(ci, CADY_SCENE_ID_ATTR))
}
